/* eslint-disable security/detect-object-injection */
import { Action } from 'infra/types'
import { getNormalizedList, getDeNormalizedList } from 'utils/dataNormalizer'
import { withDerivedActivity } from 'utils/lastActivity'
import { withPreservedDueDate } from 'utils/dueDate'
import { NEWDASHBOARD_EVENTS } from 'state/new-summary/events/events.types'
import {
  ENTITY_TYPE,
  IFilterEntity,
  InitialFilterValues,
  SupplierInitialFilterValues
} from '../../constants'
import { persistSortFilters, persistSupplierSortFilters, DashboardState } from './dashboard.state'
import { DashboardActions } from './dashboard.actions'
import { ICustomer, IDashboardState, ISupplier } from './dashboard.types'
import { applyFilters, applyFiltersOnCustomers } from './helpers/applyFilters'
import { applySearch } from './helpers/applySearch'
import { validateMerchantProfileField } from './helpers/MerchantProfile'
import { validateEntityProfileField } from './helpers/CustomerSupplierProfile'

// Re-applies the active sort to a customer map and returns the new `filtered` id order.
// Used after due dates change so the "Default" sort reflects them without waiting for
// the next filter/list action.
const resortCustomerFiltered = (
  customers: IDashboardState['customers'],
  filters: IFilterEntity[]
): string[] | undefined => {
  const ids = customers.filtered?.length ? customers.filtered : customers.allIds
  const denorm = getDeNormalizedList(customers, ids) as ICustomer[]
  return applyFilters(filters, denorm, []).customers?.map((customer) => customer.id)
}

// Supplier counterpart of resortCustomerFiltered — suppliers sort on their own tab's
// selection (supplierFilters), never the customer one.
const resortSupplierFiltered = (
  suppliers: IDashboardState['suppliers'],
  supplierFilters: IFilterEntity[]
): string[] | undefined => {
  const ids = suppliers.filtered?.length ? suppliers.filtered : suppliers.allIds
  const denorm = getDeNormalizedList(suppliers, ids) as ISupplier[]
  return applyFilters(supplierFilters, [], denorm).suppliers?.map((supplier) => supplier.id)
}

const resortCustomersByTags = (
  customersByTags: ICustomer[] | undefined,
  tagsFilter: IFilterEntity[],
  dueDateUpdates: Map<string, number | undefined>
): ICustomer[] | undefined => {
  if (!customersByTags || customersByTags.length === 0) {
    return customersByTags
  }
  const merged = customersByTags.map((customer) =>
    dueDateUpdates.has(customer.id)
      ? { ...customer, dueDate: dueDateUpdates.get(customer.id) }
      : customer
  )
  return applyFiltersOnCustomers(tagsFilter, merged).customers
}

export const DashboardReducers = (
  state: IDashboardState = DashboardState,
  action: Action<any>
): IDashboardState => {
  switch (action.type) {
    case DashboardActions.FETCH_LOCAL_DATA_SUCCESS: {
      return {
        ...state,
        merchantRegistration: {
          ...state.merchantRegistration,
          optedOut: action.payload
        }
      }
    }
    case DashboardActions.SET_BUSINESS_SETTINGS: {
      return {
        ...state,
        businessSettings: action.payload
      }
    }
    case DashboardActions.ADD_ENTITY: {
      return {
        ...state,
        entityAction: {
          ...state.entityAction,
          loading: true
        }
      }
    }
    case DashboardActions.ADD_ENTITY_SUCCESS: {
      return {
        ...state,
        entityAction: {
          ...state.entityAction,
          loading: false
        }
      }
    }
    case DashboardActions.REGISTER_MERCHANT_NAME: {
      return {
        ...state,
        merchantRegistration: {
          ...state.merchantRegistration,
          loading: true,
          optedOut: true
        }
      }
    }
    case DashboardActions.REGISTER_MERCHANT_NAME_SUCCESS: {
      return {
        ...state,
        merchantRegistration: {
          loading: false,
          show: false,
          optedOut: true
        }
      }
    }
    case DashboardActions.REGISTER_MERCHANT_NAME_FAILURE: {
      return {
        ...state,
        merchantRegistration: {
          ...state.merchantRegistration,
          loading: false,
          optedOut: true
        }
      }
    }
    case DashboardActions.SHOW_BUSINESS_NAME_MODAL: {
      return {
        ...state,
        merchantRegistration: {
          ...state.merchantRegistration,
          show: true
        }
      }
    }
    case DashboardActions.BUSINESS_NAME_MODAL_OPTED_OUT: {
      return {
        ...state,
        merchantRegistration: {
          ...state.merchantRegistration,
          show: false,
          optedOut: true
        }
      }
    }
    case DashboardActions.FETCH_BUSINESS_TYPES_SUCCESS: {
      return {
        ...state,
        businessTypes: action.payload
      }
    }
    case DashboardActions.FETCH_BUSINESS_CATEGORIES_SUCCESS: {
      const { payload } = action
      return {
        ...state,
        // @todo: Others category throws error from backend
        businessCategories: Array.isArray(payload) ? payload.filter((_) => _.type !== 2) : []
      }
    }
    case DashboardActions.FETCH_MERCHANT_PROFILE: {
      return {
        ...state,
        merchantProfile: {
          ...state.merchantProfile,
          loading: true
        }
      }
    }
    case DashboardActions.FETCH_MERCHANT_PROFILE_SUCCESS: {
      const merchantProfile = action.payload

      return {
        ...state,
        merchantProfile: {
          ...state.merchantProfile,
          data: merchantProfile,
          edited: {
            values: {
              name: merchantProfile.name,
              mobile: merchantProfile.mobile,
              address: merchantProfile.address,
              email: merchantProfile.email,
              contactName: merchantProfile.contactName,
              about: merchantProfile.about,
              businessTypeId: merchantProfile.businessTypeId,
              categoryId: merchantProfile.categoryId,
              gst: merchantProfile.businessGst,
              udyamNumber: merchantProfile.udyamNumber
            },
            errors: {}
          },
          loading: false
        }
      }
    }
    case DashboardActions.FETCH_MERCHANT_CONFIG_SUCCESS: {
      return {
        ...state,
        currentBusinessConfig: action.payload
      }
    }
    case DashboardActions.FETCH_MERCHANT_PROFILE_FAILURE: {
      return {
        ...state,
        merchantProfile: {
          ...state.merchantProfile,
          loading: false
        }
      }
    }
    case DashboardActions.EDIT_MERCHANT_PROFILE: {
      const { key, value } = action.payload
      let errorMsg = ''
      if (key === 'name' && value.length > 45) {
        errorMsg = 'Max character limit reached'
        return {
          ...state,
          merchantProfile: {
            ...state.merchantProfile,
            ...(state.merchantProfile.edited
              ? {
                  edited: {
                    values: {
                      ...state.merchantProfile.edited.values
                    },
                    errors: {
                      ...state.merchantProfile.edited.errors,
                      [key]: errorMsg
                    }
                  }
                }
              : {}),
            loading: true
          }
        }
      }
      return {
        ...state,
        merchantProfile: {
          ...state.merchantProfile,
          ...(state.merchantProfile.edited
            ? {
                edited: {
                  values: {
                    ...state.merchantProfile.edited.values,
                    [key]: value
                  },
                  errors: {
                    ...state.merchantProfile.edited.errors,
                    [key]: !validateMerchantProfileField({ key, value })
                  }
                }
              }
            : {}),
          loading: true
        }
      }
    }
    case DashboardActions.PATCH_MERCHANT_PROFILE: {
      const profile = action.payload
      return {
        ...state,
        merchantProfile: {
          ...state.merchantProfile,
          data: {
            ...state.merchantProfile.data,
            ...profile
          },
          edited: {
            values: {
              ...state.merchantProfile.edited?.values,
              name: profile.name,
              mobile: profile.mobile,
              address: profile.address,
              email: profile.email,
              contactName: profile.contactName,
              about: profile.about,
              businessTypeId: profile.businessTypeId,
              categoryId: profile.categoryId
            },
            errors: {}
          },
          loading: false
        },
        businessAccounts: {
          ...state.businessAccounts,
          businessDetails: {
            ...state.businessAccounts.businessDetails,
            [profile.id]: profile
          }
        }
      }
    }
    case DashboardActions.UPDATE_SEARCH_STRING: {
      const customers = getDeNormalizedList(state.customers) as ICustomer[]
      const suppliers = getDeNormalizedList(state.suppliers) as ISupplier[]
      const searchResults = applySearch(action.payload, state.currentTab, customers, suppliers)
      // Customers and suppliers each sort by their own tab's selection.
      const filteredCustomersIds = applyFilters(
        state.filters,
        searchResults.customers,
        []
      ).customers?.map((_) => _.id)
      const filteredSuppliersIds = applyFilters(
        state.supplierFilters,
        [],
        searchResults.suppliers
      ).suppliers?.map((_) => _.id)

      return {
        ...state,
        searchString: action.payload,
        customers: {
          ...state.customers,
          filtered: filteredCustomersIds
        },
        suppliers: {
          ...state.suppliers,
          filtered: filteredSuppliersIds
        }
      }
    }
    case DashboardActions.UPDATE_SEARCH_STRING_ON_TAGS: {
      return {
        ...state,
        searchString: action.payload
      }
    }
    case DashboardActions.CLEAR_FILTERS: {
      // Reset only the active tab's sort to its default (customer → Default,
      // supplier → Latest); the other tab keeps its selection.
      const customers = getDeNormalizedList(state.customers) as ICustomer[]
      const suppliers = getDeNormalizedList(state.suppliers) as ISupplier[]
      const searchResults = applySearch(state.searchString, state.currentTab, customers, suppliers)
      if (state.currentTab === ENTITY_TYPE.CUSTOMER) {
        persistSortFilters(InitialFilterValues)
        return {
          ...state,
          filters: InitialFilterValues,
          customers: {
            ...state.customers,
            filtered: applyFilters(InitialFilterValues, searchResults.customers, []).customers?.map(
              (_) => _.id
            )
          }
        }
      }
      persistSupplierSortFilters(SupplierInitialFilterValues)
      return {
        ...state,
        supplierFilters: SupplierInitialFilterValues,
        suppliers: {
          ...state.suppliers,
          filtered: applyFilters(
            SupplierInitialFilterValues,
            [],
            searchResults.suppliers
          ).suppliers?.map((_) => _.id)
        }
      }
    }
    case DashboardActions.SAVE_FILTERS: {
      // Customer tab only — suppliers keep their own sort (supplierFilters).
      const filteredCustomers = getDeNormalizedList(
        state.customers,
        state.customers.filtered || []
      ) as ICustomer[]
      const filteredCustomersIds = applyFilters(
        action.payload,
        filteredCustomers,
        []
      ).customers?.map((_) => _.id)
      // Persist the user's sort/filter choice so it survives page reloads.
      persistSortFilters(action.payload)
      return {
        ...state,
        filters: action.payload,
        customers: {
          ...state.customers,
          filtered: filteredCustomersIds
        }
      }
    }
    case DashboardActions.SAVE_SUPPLIER_FILTERS: {
      // Supplier tab only.
      const filteredSuppliers = getDeNormalizedList(
        state.suppliers,
        state.suppliers.filtered || []
      ) as ISupplier[]
      const filteredSuppliersIds = applyFilters(
        action.payload,
        [],
        filteredSuppliers
      ).suppliers?.map((_) => _.id)
      // Persist the supplier sort so it survives page reloads (mirrors customers).
      persistSupplierSortFilters(action.payload)
      return {
        ...state,
        supplierFilters: action.payload,
        suppliers: {
          ...state.suppliers,
          filtered: filteredSuppliersIds
        }
      }
    }
    case DashboardActions.SAVE_FILTERS_ON_TAGS: {
      const filteredData = applyFiltersOnCustomers(action.payload, state.customersByTags)
      return {
        ...state,
        customersByTags: filteredData.customers,
        tagsFilter: action.payload
      }
    }
    case DashboardActions.CHANGE_LIST_TAB: {
      return {
        ...state,
        currentTab: action.payload,
        searchString: ''
      }
    }
    case DashboardActions.FETCH_ENTITY_LIST: {
      const entityType = action.payload

      return {
        ...state,
        ...(entityType === ENTITY_TYPE.CUSTOMER
          ? {
              customers: {
                ...state.customers,
                loading: true
              }
            }
          : {
              suppliers: {
                ...state.suppliers,
                loading: true
              }
            })
      }
    }
    case DashboardActions.FETCH_ENTITY_LIST_SUCCESS: {
      const isCustomer = action.payload.entityType === ENTITY_TYPE.CUSTOMER
      const entities = action.payload.entities
      const normalisedEntities = getNormalizedList(entities)

      if (isCustomer) {
        // The API's last_activity can lag the synced transactions; reconcile before sorting
        // so a refetch doesn't undo what SET_LAST_ACTIVITY already corrected.
        // Same story for due dates, which this response omits entirely — carry the ones we
        // already know so the Default sort keeps its due-date buckets instead of collapsing
        // to plain recency for the ~125ms until SET_CUSTOMER_DUES_INFO lands.
        const withDues = withPreservedDueDate(
          entities as ICustomer[],
          state.customers.byIds
        ) as ICustomer[]
        const withActivity = withDerivedActivity(
          withDues,
          state.customerLastActivity
        ) as ICustomer[]
        const filteredData = applyFilters(
          state.filters,
          withActivity,
          state.suppliers.filtered?.length
            ? getDeNormalizedList(state.suppliers, state.suppliers.filtered)
            : undefined
        )
        const filteredIds = filteredData.customers?.map((_) => _.id)
        return {
          ...state,
          customers: {
            ...getNormalizedList(withActivity),
            filtered: filteredIds,
            loading: false
          }
        }
      }
      // Suppliers carry the same stale-`last_activity_time` problem as customers.
      const suppliersWithActivity = withDerivedActivity(
        entities as ISupplier[],
        state.supplierLastActivity
      ) as ISupplier[]
      const filteredData = applyFilters(
        state.supplierFilters,
        state.customers.filtered?.length
          ? getDeNormalizedList(state.customers, state.customers.filtered)
          : undefined,
        suppliersWithActivity
      )
      const filteredIds = filteredData.suppliers?.map((_) => _.id)
      return {
        ...state,
        suppliers: {
          ...getNormalizedList(suppliersWithActivity),
          filtered: filteredIds,
          loading: false
        }
      }
    }
    case DashboardActions.SET_CUSTOMER_DUES_INFO: {
      const dues: Array<{ customerId: string; dueDate: number }> = action.payload || []
      if (!dues.length) {
        return state
      }
      const byIds = { ...state.customers.byIds }
      const dueDateUpdates = new Map<string, number | undefined>()
      dues.forEach(({ customerId, dueDate }) => {
        dueDateUpdates.set(customerId, dueDate)
        if (byIds[customerId]) {
          byIds[customerId] = { ...byIds[customerId], dueDate }
        }
      })
      // This response is the full set of active dues, so anything holding a due date that
      // it does not mention has been cleared (here or on another device) and must drop it.
      // The entity-list refetch used to do this implicitly by blanking every dueDate;
      // withPreservedDueDate stops that, so the clearing has to happen explicitly.
      Object.keys(byIds).forEach((customerId) => {
        if (byIds[customerId]?.dueDate !== undefined && !dueDateUpdates.has(customerId)) {
          byIds[customerId] = { ...byIds[customerId], dueDate: undefined }
          dueDateUpdates.set(customerId, undefined)
        }
      })
      const updatedCustomers = { ...state.customers, byIds }
      return {
        ...state,
        customers: {
          ...updatedCustomers,
          filtered: resortCustomerFiltered(updatedCustomers, state.filters)
        },
        customersByTags: resortCustomersByTags(
          state.customersByTags,
          state.tagsFilter,
          dueDateUpdates
        )
      }
    }
    case DashboardActions.UPDATE_CUSTOMER_DUE_DATE: {
      const { customerId, dueDate } = action.payload
      const existingCustomer = state.customers.byIds[customerId]
      if (!existingCustomer) {
        return state
      }
      const nextDueDate = dueDate === null ? undefined : dueDate
      const updatedCustomers = {
        ...state.customers,
        byIds: {
          ...state.customers.byIds,
          [customerId]: {
            ...existingCustomer,
            dueDate: nextDueDate
          }
        }
      }
      return {
        ...state,
        customers: {
          ...updatedCustomers,
          filtered: resortCustomerFiltered(updatedCustomers, state.filters)
        },
        customersByTags: resortCustomersByTags(
          state.customersByTags,
          state.tagsFilter,
          new Map([[customerId, nextDueDate]])
        )
      }
    }
    case DashboardActions.SET_LAST_ACTIVITY: {
      const { entityType, map } = action.payload
      if (entityType !== ENTITY_TYPE.CUSTOMER) {
        const mergedSuppliers = withDerivedActivity(
          getDeNormalizedList(state.suppliers) as ISupplier[],
          map || {}
        ) as ISupplier[]
        const supplierByIds = { ...state.suppliers.byIds }
        mergedSuppliers.forEach((supplier) => {
          supplierByIds[supplier.id] = supplier
        })
        const suppliers = { ...state.suppliers, byIds: supplierByIds }
        return {
          ...state,
          supplierLastActivity: map || {},
          suppliers: {
            ...suppliers,
            filtered: resortSupplierFiltered(suppliers, state.supplierFilters)
          }
        }
      }
      // Fold the freshly derived activity into the customer records and re-sort, so the
      // ledger order picks it up as soon as the transaction cache is read rather than on
      // the next list fetch. Without this the rows would be labelled from the cache but
      // still ordered by the API's staler last_activity.
      const merged = withDerivedActivity(
        getDeNormalizedList(state.customers) as ICustomer[],
        map || {}
      ) as ICustomer[]
      const byIds = { ...state.customers.byIds }
      merged.forEach((customer) => {
        byIds[customer.id] = customer
      })
      const customers = { ...state.customers, byIds }
      const mergedByTags = withDerivedActivity(state.customersByTags, map || {})
      return {
        ...state,
        customerLastActivity: map || {},
        customers: {
          ...customers,
          filtered: resortCustomerFiltered(customers, state.filters)
        },
        customersByTags: mergedByTags
          ? applyFiltersOnCustomers(state.tagsFilter, mergedByTags).customers
          : mergedByTags
      }
    }
    case DashboardActions.ADJUST_ENTITY_BALANCE_LOCALLY: {
      const { entityType, entityId, delta } = action.payload
      const key = entityType === ENTITY_TYPE.CUSTOMER ? 'customers' : 'suppliers'
      const existing = state[key].byIds[entityId]
      if (!existing) {
        return state
      }
      return {
        ...state,
        [key]: {
          ...state[key],
          byIds: {
            ...state[key].byIds,
            [entityId]: {
              ...existing,
              balance: Number(existing.balance) + delta
            }
          }
        }
      }
    }
    case DashboardActions.FETCH_ENTITY_LIST_FAILURE: {
      const entityType = action.payload

      return {
        ...state,
        ...(entityType === ENTITY_TYPE.CUSTOMER
          ? {
              customers: {
                ...state.customers,
                loading: false
              }
            }
          : {
              suppliers: {
                ...state.suppliers,
                loading: false
              }
            })
      }
    }
    case DashboardActions.EDIT_ENTITY: {
      const { key, value, entityType, entityId } = action.payload
      const newState = { ...state }
      if (entityType === ENTITY_TYPE.CUSTOMER) {
        newState.customers = {
          ...state.customers,
          byIds: {
            ...state.customers.byIds,
            ...(state.customers.byIds[entityId]
              ? {
                  [entityId]: {
                    ...state.customers?.byIds[entityId],
                    edited: {
                      values: {
                        ...state.customers.byIds[entityId].edited.values,
                        [key]: value
                      },
                      errors: {
                        ...state.customers.byIds[entityId].edited.errors,
                        [key]: !validateEntityProfileField({ key, value })
                      }
                    }
                  }
                }
              : {})
          }
        }
      } else if (state.suppliers) {
        newState.suppliers = {
          ...state.suppliers,
          byIds: {
            ...state.suppliers.byIds,
            ...(state.suppliers.byIds[entityId]
              ? {
                  [entityId]: {
                    ...state.suppliers.byIds[entityId],
                    edited: {
                      values: {
                        ...state.suppliers.byIds[entityId].edited.values,
                        [key]: value
                      },
                      errors: {
                        ...state.suppliers.byIds[entityId].edited.errors,
                        [key]: !validateEntityProfileField({ key, value })
                      }
                    }
                  }
                }
              : {})
          }
        }
      }
      return newState
    }
    case DashboardActions.PATCH_ENTITY: {
      const { entityType, id: entityId } = action.payload
      const newState = { ...state }

      if (entityType === ENTITY_TYPE.CUSTOMER) {
        newState.customers = {
          ...state.customers,
          byIds: {
            ...state.customers.byIds,
            [entityId]: action.payload
          }
        }
      } else if (state.suppliers) {
        newState.suppliers = {
          ...state.suppliers,
          byIds: {
            ...state.suppliers.byIds,
            [entityId]: action.payload
          }
        }
      }
      return newState
    }
    case DashboardActions.DELETE_ENTITY_SUCCESS: {
      const { entityType, id } = action.payload
      const filter = (_: string) => _ !== id

      return {
        ...state,
        ...(entityType === ENTITY_TYPE.CUSTOMER
          ? {
              customers: {
                ...state.customers,
                allIds: state.customers.allIds.filter(filter),
                filtered: state.customers.filtered?.filter(filter)
              }
            }
          : {
              suppliers: {
                ...state.suppliers,
                allIds: state.suppliers.allIds.filter(filter),
                filtered: state.suppliers.filtered?.filter(filter)
              }
            })
      }
    }
    case DashboardActions.FETCH_PHONEBOOK_CONTACTS: {
      return {
        ...state,
        phonebookContacts: {
          ...state.phonebookContacts,
          loading: true
        }
      }
    }
    case DashboardActions.FETCH_PHONEBOOK_CONTACTS_SUCCESS: {
      return {
        ...state,
        phonebookContacts: {
          data: action.payload,
          loading: false
        }
      }
    }
    case DashboardActions.FETCH_PHONEBOOK_CONTACTS_FAILURE: {
      return {
        ...state,
        phonebookContacts: {
          ...state.phonebookContacts,
          loading: false
        }
      }
    }
    case DashboardActions.FETCH_HELP_SECTIONS: {
      return {
        ...state,
        helpSections: {
          ...state.helpSections,
          loading: true
        }
      }
    }
    case DashboardActions.FETCH_HELP_SECTIONS_SUCCESS: {
      return {
        ...state,
        helpSections: {
          data: action.payload,
          loading: false
        }
      }
    }
    case DashboardActions.FETCH_HELP_SECTIONS_FAILURE: {
      return {
        ...state,
        helpSections: {
          ...state.helpSections,
          loading: false
        }
      }
    }
    case DashboardActions.GET_CUSTOMER_DATA_SUCCESS: {
      return {
        ...state,
        currentCustomer: action.payload
      }
    }
    case DashboardActions.SET_SHOW_LEADGEN: {
      return {
        ...state,
        showLeadgenBanner: action.payload
      }
    }
    case DashboardActions.SHOW_TXN_ALERT_NUDGE: {
      return { ...state, txnAlertNudge: action.payload }
    }
    case DashboardActions.HIDE_TXN_ALERT_NUDGE: {
      return { ...state, txnAlertNudge: null }
    }
    case DashboardActions.SET_ACCOUNT_PROFILE_IMAGE: {
      const { entityId, entityType, url } = action.payload
      const bucket = entityType === ENTITY_TYPE.CUSTOMER ? 'customers' : 'suppliers'
      const current = state[bucket].byIds[entityId]
      if (!current) {
        return state
      }
      return {
        ...state,
        [bucket]: {
          ...state[bucket],
          byIds: { ...state[bucket].byIds, [entityId]: { ...current, profileImage: url } }
        }
      }
    }
    case DashboardActions.SET_PENDING_TXN_ALERT_UPGRADE: {
      return { ...state, pendingTxnAlertUpgrade: action.payload }
    }
    case DashboardActions.CLEAR_PENDING_TXN_ALERT_UPGRADE: {
      return { ...state, pendingTxnAlertUpgrade: null }
    }
    case DashboardActions.MARK_TXN_ALERT_SETTING_SEEN: {
      // Optimistic: the account is never nudged again in this session even if the PATCH lags
      const { entityId, entityType } = action.payload
      const bucket = entityType === ENTITY_TYPE.CUSTOMER ? 'customers' : 'suppliers'
      const current = state[bucket].byIds[entityId]
      if (!current) {
        return state
      }
      return {
        ...state,
        [bucket]: {
          ...state[bucket],
          byIds: {
            ...state[bucket].byIds,
            [entityId]: { ...current, displayTxnAlertSetting: false }
          }
        }
      }
    }
    case DashboardActions.FETCH_FILTERED_CUSTOMER_LIST_SUCCESS: {
      return {
        ...state,
        customersByTags: action.payload
      }
    }
    case DashboardActions.SAVE_SEARCH_BY_FILTERS: {
      return {
        ...state,
        searchFilters: action.payload
      }
    }
    case DashboardActions.FETCH_SEARCH_BY_INVOICE_DATA_SUCCESS: {
      return {
        ...state,
        billsFiltered: action.payload
      }
    }
    case DashboardActions.SET_BUSINESS_ACCOUNTS_LOADER: {
      return {
        ...state,
        businessAccounts: {
          ...state.businessAccounts,
          loading: action.payload
        }
      }
    }
    case DashboardActions.SET_BUSINESS_ACCOUNTS_DRAWER: {
      return {
        ...state,
        businessAccounts: {
          ...state.businessAccounts,
          drawers: {
            ...state.businessAccounts.drawers,
            ...action.payload
          }
        }
      }
    }
    case DashboardActions.FETCH_BUSINESS_IDS_SUCCESS: {
      return {
        ...state,
        businessAccounts: {
          ...state.businessAccounts,
          businessIds: action.payload.businessIds,
          individualUser: action.payload.individualUser,
          asStaffBusinessIds: action.payload.asStaffBusinessIds,
          permittedWorkflows: action.payload.permittedWorkflows
        }
      }
    }
    case DashboardActions.SET_BUSINESS_DETAILS: {
      return {
        ...state,
        businessAccounts: {
          ...state.businessAccounts,
          businessDetails: action.payload
        }
      }
    }
    case DashboardActions.SET_BRAND_DATA: {
      return {
        ...state,
        brandData: action.payload
      }
    }
    case DashboardActions.SET_BUSINESS_CONFIG: {
      return {
        ...state,
        businessConfigWithBusinessId: action.payload.data
      }
    }
    case DashboardActions.SET_AS_STAFF_BUSINESS_DETAILS: {
      return {
        ...state,
        businessAccounts: {
          ...state.businessAccounts,
          asStaffBusinessDetails: action.payload
        }
      }
    }
    case DashboardActions.CREATE_BUSINESS_SUCCESS: {
      const businessId = action.payload.id
      return {
        ...state,
        businessAccounts: {
          ...state.businessAccounts,
          businessIds: [...state.businessAccounts.businessIds, businessId],
          businessDetails: {
            ...state.businessAccounts.businessDetails,
            [businessId]: action.payload
          }
        }
      }
    }
    case DashboardActions.SET_BUSINESS_MANAGERS: {
      return {
        ...state,
        businessManagers: action.payload
      }
    }
    case DashboardActions.ADD_BUSINESS_MANAGER:
    case DashboardActions.DELETE_BUSINESS_MANAGER: {
      return {
        ...state,
        isBusinessManagerActionInProgress: true
      }
    }
    case DashboardActions.SET_BUSINESS_MANAGER_ACTION_IN_PROGRESS: {
      return {
        ...state,
        isBusinessManagerActionInProgress: action.payload
      }
    }
    case DashboardActions.BULK_UPLOAD_ACCOUNTS_SUCCESS: {
      return {
        ...state,
        accountsDocument: {
          documentId: action.payload
        }
      }
    }
    case DashboardActions.BULK_UPLOAD_ACCOUNTS_STATUS_SUCCESS: {
      if (!state.accountsDocument) {
        return state
      }
      return {
        ...state,
        accountsDocument: {
          ...state.accountsDocument,
          document_status: action.payload
        }
      }
    }
    case DashboardActions.SET_LOADER: {
      return {
        ...state,
        loaders: {
          ...state.loaders,
          [action.payload.id]: action.payload.value
        }
      }
    }
    case DashboardActions.SET_CUSTOMER_PROFILES_DRAWER: {
      return {
        ...state,
        customerProfiles: {
          ...state.customerProfiles,
          drawers: {
            ...state.customerProfiles.drawers,
            [action.payload.id]: {
              status: action.payload.value,
              replace: action.payload.replace || false
            }
          }
        }
      }
    }
    case NEWDASHBOARD_EVENTS.CLOSE_PAID_SOON_BANNER: {
      return {
        ...state,
        paidSoonBanner: {
          ...state.paidSoonBanner,
          isShow: false
        }
      }
    }
    case NEWDASHBOARD_EVENTS.OPEN_PAID_SOON_BANNER: {
      return {
        ...state,
        paidSoonBanner: {
          ...state.paidSoonBanner,
          isShow: true
        }
      }
    }
    case DashboardActions.SET_CUSTOMER_PROFILES_DATA: {
      return {
        ...state,
        customerProfiles: {
          ...state.customerProfiles,
          ...action.payload
        }
      }
    }
    default: {
      return state
    }
  }
}
